import {
  EmptyStatePage,
  GlassCard,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const CARD_SHADOW =
  "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.28),inset_0_-1px_0_0_rgb(255_255_255/0.06),0_18px_44px_-16px_rgb(0_0_0/0.65)]";

type ReportsEmptyStateProps = {
  onBuildReport: () => void;
};

export function ReportsEmptyState({ onBuildReport }: ReportsEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);

  return (
    <EmptyStatePage
      eyebrow="Reports"
      headline="Stop rebuilding the same reports"
      headlineNoWrap
      subline="Pick the metrics and breakdowns you care about, then pull the same report every month without touching it again."
      measureCh={46}
      contentGapClassName="gap-12"
      portraitSrc="/empty-states/portrait-reports.webp"
      portraitMaskLeft="30%"
      portraitClassName="-right-20 bottom-0 h-[95%] w-auto opacity-40"
      actionsDelaySeconds={1.35}
      primaryAction={{ label: "Build your first report", onClick: onBuildReport }}
      device={
        <div className="w-full max-w-[380px]">
          <GlassCard
            radius="2xl"
            className={`w-full p-4 backdrop-blur-3xl ${CARD_SHADOW}`}
            style={{ animation: entrance.glassIn(0.3) }}
            overlayStyle={{ animation: entrance.fadeIn(0.3) }}
          >
            <div style={{ animation: entrance.fadeInUp(0.3) }}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm leading-5 text-white">Product GMV by revenue source</p>
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="shrink-0 text-white/70"
                >
                  <path d="M8 13v4m8-6v6M12 7v10m-4.2 4h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 18.72 21 17.88 21 16.2V7.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 3 17.88 3 16.2 3H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 21 6.12 21 7.8 21Z" />
                </svg>
              </div>
              <svg
                aria-hidden="true"
                viewBox="0 0 340 160"
                className="mt-3 h-auto w-full"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M 18 10 L 18 140 L 336 140"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="text-white/85"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d="M 18 132 C 60 132 76 60 118 60 C 158 60 166 100 198 98 C 232 96 248 70 272 55 C 300 38 316 26 332 18"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-white"
                  vectorEffect="non-scaling-stroke"
                />
                <circle
                  cx="272"
                  cy="55"
                  r="6"
                  className="fill-white"
                  style={{ animation: reduceMotion ? undefined : "fade-in-up 0.4s ease-out 1.65s both" }}
                />
              </svg>
              <div className="mt-3 flex items-center justify-between gap-4 border-t border-white/15 pt-3">
                <span className="text-[13px] text-brand-100">GMV:</span>
                <span className="text-[13px] font-semibold text-white">£12.7K</span>
              </div>
            </div>
          </GlassCard>
        </div>
      }
    />
  );
}
